<template>
  <div :class="statisticsClasses">
    <div class="statistics__message" v-if="isToday">
      Die nachfolgenden Trading-Daten sind von
      <strong>heute {{ dataTime }} Uhr</strong> und werden mehrmals täglich
      aktualisiert.
    </div>
    <template v-if="isProducer">
      <h1 class="statistics__overview-title">Mein Energiemix</h1>
      <div class="statistics__overview-consumption">
        <PieChart
          v-if="dataAvailable"
          headline="Verbrauch"
          data-source="consumedEnergyTypes"
        >
          <template v-slot:content>
            <div class="pie-chart__content">
              Am {{ formattedDate }} bezogen:
              <strong>{{ formatKWH(totalConsumed) }}</strong>
            </div>
          </template>
        </PieChart>
      </div>
      <div class="statistics__overview-production">
        <PieChart
          v-if="dataAvailable"
          headline="Produktion"
          data-source="producedEnergy"
        >
          <template v-slot:content>
            <div class="pie-chart__content">
              Am {{ formattedDate }} eingespeist:
              <strong>{{ formatKWH(totalProduced) }}</strong>
            </div>
          </template>
        </PieChart>
      </div>
    </template>
    <div v-else class="statistics__overview">
      <PieChart
        v-if="dataAvailable"
        headline="Mein Energiemix"
        data-source="consumedEnergyTypes"
      >
        <template v-slot:content>
          <div class="pie-chart__content">
            Am {{ formattedDate }} bezogen:
            <strong>{{ formatKWH(totalConsumed) }}</strong>
          </div>
        </template>
      </PieChart>
    </div>

    <div class="statistics__producers">
      <PieChart
        v-if="dataAvailable"
        headline="Meine Produzenten"
        data-source="producers"
      >
        <template v-slot:content>
          <div class="pie-chart__content">
            Am {{ formattedDate }} bezogen:
            <strong>{{ formatKWH(producersTotal) }}</strong>
          </div>
        </template>
      </PieChart>

      <TradingStatistics
        v-if="dataAvailable"
        data-source="producers"
        :is-producers="true"
      />
    </div>

    <div class="statistics__consumers" v-if="hasConsumers">
      <PieChart
        v-if="dataAvailable"
        headline="Meine Konsumenten"
        data-source="consumers"
      >
        <template v-slot:content>
          <div class="pie-chart__content">
            Am {{ formattedDate }} geliefert:
            <strong>{{ formatKWH(consumersTotal) }}</strong>
          </div>
        </template>
      </PieChart>

      <TradingStatistics
        v-if="dataAvailable"
        data-source="consumers"
        :is-producers="false"
        energy-type="pv"
      />
    </div>

    <div class="statistics__message--unobtrusive">
      Produktions- bzw Bezugswerte unter 0,1kWh werden aus Gründen der
      Übersichtlichkeit nicht dargestellt.
    </div>
  </div>
</template>

<script>
  import PieChart from './PieChart'
  import TradingStatistics from './TradingStatistics'
  import moment from 'moment'

  export default {
    name: 'power-statistics',
    components: {
      PieChart,
      TradingStatistics,
    },
    computed: {
      statisticsClasses() {
        let classes = ['statistics']
        if (this.hasConsumers) {
          classes.push('statistics--has-consumers')
        }
        return classes.join(' ')
      },
      dataAvailable() {
        return this.$store.getters.tradingStatisticsReceived
      },
      isProducer() {
        return this.$store.getters.hasProduction
      },
      formattedDate() {
        return moment(this.$store.getters.historySelectedDate).format(
          'DD.MM.YYYY'
        )
      },
      producersTotal() {
        return this.$store.getters.tradingStatisticsAggregate(
          'consumedFromCommunity'
        )
      },
      consumersTotal() {
        return this.$store.getters.tradingStatisticsAggregate(
          'producedForCommunity'
        )
      },
      totalConsumed() {
        const community = this.$store.getters.tradingStatisticsAggregate(
          'consumedFromCommunity'
        )
        const retailer = this.$store.getters.tradingStatisticsAggregate(
          'consumedFromRetailer'
        )
        return community + retailer
      },
      totalProduced() {
        const community = this.$store.getters.tradingStatisticsAggregate(
          'producedForCommunity'
        )
        const retailer = this.$store.getters.tradingStatisticsAggregate(
          'producedForRetailer'
        )
        return community + retailer
      },
      hasConsumers() {
        return (
          this.$store.getters.tradingStatistics('consumers').data.length > 0
        )
      },
      isToday() {
        return moment(this.$store.getters.tradingStatisticsLastUpdate).isSame(
          new Date(),
          'day'
        )
      },
      dataTime() {
        return moment(this.$store.getters.tradingStatisticsLastUpdate).format(
          'HH:mm'
        )
      },
    },
  }
</script>

<style lang="scss">
  .statistics {
    margin-top: spacer(2);

    @media (min-width: $bp-ml) {
      margin-top: spacer(4);

      &--has-consumers {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
      }
    }

    &__message {
      box-shadow: 2px 2px 7px rgba(0, 0, 0, 0.15);
      padding: spacer(1) spacer(3) spacer(1) spacer(2);
      border-left: 4px solid $community;
      background: $white;
      margin: spacer(2) auto;
      max-width: 800px;

      &--unobtrusive {
        font-size: $font-size-sm;
        line-height: $line-height-sm;
        margin: spacer(2) auto;
        color: $grey-dark;
        text-align: center;
      }

      p {
        margin: 0;
      }
    }

    &__overview {
      flex-basis: 100%;
      margin-bottom: spacer(3);

      @media (min-width: $bp-ml) {
        margin-bottom: spacer(5);
      }

      &-title {
        flex-basis: 100%;
      }
    }

    &__overview-consumption,
    &__overview-production {
      @media (min-width: $bp-ml) {
        margin-bottom: spacer(5);
      }
    }

    &__overview-consumption,
    &__overview-production,
    &__consumers,
    &__producers {
      .statistics--has-consumers & {
        @media (min-width: $bp-ml) {
          flex-basis: 45%;
          width: 45%;
        }
      }
    }
  }
</style>
